/* oxlint-disable @typescript-eslint/no-explicit-any */
/* oxlint-disable @typescript-eslint/no-use-before-define */

import {
  postUserPasskeyAuthenticationOptionsApi,
  postUserPasskeyRegistrationApi,
  postUserPasskeyRegistrationOptionsApi,
} from '@flipster/apis/v1/user';
import { getDevice, getOs } from '@flipster/utils/user-agent';

export function isPasskeySupported(): boolean {
  return typeof window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable === 'function';
}

export async function registerPasskey(mfaSessionToken: string): Promise<void> {
  const credential = await createCredential();
  const rawId = toBase64(credential.rawId);
  await postUserPasskeyRegistrationApi({
    id: credential.id,
    rawId,
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    response: {
      clientDataJSON: toBase64(credential.response.clientDataJSON),
      attestationObject: toBase64((credential.response as any).attestationObject),
    },
    decodeChallenge: true,
    mfaSessionToken,
  });
}

export async function createCredential(): Promise<PublicKeyCredential> {
  const device = getDevice();
  const os = getOs();
  const adid = ''; // context: https://prextech.slack.com/archives/C07BZ2P9BHR/p1725325862072089
  const options = await postUserPasskeyRegistrationOptionsApi({ device, os, adid });
  const credential = (await navigator.credentials.create({
    publicKey: {
      ...options.data,
      user: { ...options.data.user, id: toBufferSource(options.data.user.id) },
      excludeCredentials: options.data.excludeCredentials.map((excludeCredential) => ({
        ...excludeCredential,
        id: toBufferSource(excludeCredential.id),
      })),
      challenge: toBufferSource(options.data.challenge),
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('Failed to create credential');
  return credential;
}

export async function getCredential(): Promise<PublicKeyCredential> {
  const options = await postUserPasskeyAuthenticationOptionsApi();
  const credential = (await navigator.credentials.get({
    publicKey: {
      ...options.data,
      challenge: toBufferSource(options.data.challenge),
      // @jchoi - 2024-11-11
      // allowCredentials 값이 들어있으면 무슨 짓을 해도 인증기가 패스키를 찾지 못함
      // credential id 값은 base64 인코딩된 값이고, 서버에서 이를 urlsafe하게 변환하고 있는데,
      // 그 것 때문인가 싶어서 등록시 얻었던 credential.rawId를 그대로 allowCredentials으로 넘겨봤지만
      // 여전히 Chrome WebAuthn Emulator, 1Password, Bitwarden 전부 패스키를 찾지 못함
      // allowCredentials를 비워주면 잘 동작함
      allowCredentials: undefined,
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('Failed to get credential');
  return credential;
}

export function toBase64(arrayBuffer: ArrayBuffer): string {
  const base64 = Buffer.from(arrayBuffer).toString('base64');
  const urlsafeBase64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  return urlsafeBase64;
}

function toBufferSource(text: string): ArrayBuffer {
  return Uint8Array.from(text, (c) => c.charCodeAt(0)).buffer;
}
